import { produce } from 'immer';
import {
  OmniElement,
  OmniStyleElement,
  OmniIconElement,
  html,
  css,
  classMap,
} from 'omni-ui';
import 'omni-wysiwyg';

import './persona-base-card.js';

OmniStyleElement.register();
OmniIconElement.register();

/**
 * @summary Class for PersonaTextCard
 * @description Basic card used by {@link CampaignAudiencePersona} that utilizes {@link CampaignWYSIWYG} as a basic text editor. Saves users formatted text
 * @extends OmniElement
 */
export class PersonaTextCard extends OmniElement {
  /**
   * @property {Object} internalState - Internal state of the class before updating parent state
   * @property {Boolean} isEditView - tracks which view TextCard is on
   * @property {Boolean} readOnly - Boolean determining if content is readOnly or editable
   * @property {Object} textData - State passed down by parent component: {
      cardType: 'text-card',
      content: '',
      editorPlaceholder: 'Enter card description',
      title: 'Top Content',
    }
   */
  static get properties() {
    return {
      internalState: { type: Object },
      isEditView: { type: Boolean },
      readOnly: { type: Boolean },
      textData: { type: Object },
    };
  }

  /**
   * PersonaTextCard module styles
   */
  static get styles() {
    return [
      super.styles,
      css`
        :host,
        omni-style.omni,
        .omni persona-base-card {
          display: flex;
          flex: 1 1 auto;
          min-height: 12.57rem; /*force browsers to allow card to shrink and not overflow container*/
        }

        .content-container {
          display: flex;
          overflow: clip;
          flex: 1 1 auto;
          min-height: 0;
        }

        .content-placeholder {
          justify-content: center;
          align-items: center;
          text-align: center;
        }

        .content {
          justify-content: flex-start;
        }

        .edit-view::part(base-card-container) {
          min-width: 30.286rem;
          height: 30.714rem;
        }

        div.field > label.label {
          font-size: 0.79rem;
          color: var(--color-almost-black);
        }

        .omni div.field.input-container {
          margin-bottom: 2.14rem;
        }

        .omni div.field.text-container {
          display: flex;
          flex-flow: column nowrap;
          flex: 1 1 auto;
          min-height: 0;
          margin-bottom: 1.71rem;
        }

        div.control.text-area {
          height: 14.29rem;
        }
      `,
    ];
  }

  /**
   * Initializes {@link #internalState}, {@link #isEditView}, and {@link #readOnly}
   */
  constructor() {
    super();
    this.internalState = {};
    this.isEditView = false;
    this.readOnly = false;
  }

  /**
   * Instantiates {@link #internalState} to {@link #textData}
   */
  connectedCallback() {
    super.connectedCallback();
    this.internalState = this.textData;
  }

  /**
   * @param {CampaignWYSIWYG#_handleContentChange~event:change} event - object containing event detail which is the value from the WYSIWYG editor content
   * @summary Handles content change from WYSIWYG editor and updates {@link #internalState} using produce
   */
  _onEditorChange(event) {
    const { value } = event.detail;
    this.internalState = produce(this.internalState, draft => {
      draft.content = value;
    });
  }

  /**
   * @summary Updates {@link #textData} to the spread of {@link #internalState} and emits @event persona-change that {@link CampaignAudiencePersona#_updatePersonaData} listens to and updates the modules state with.
   */
  _updateCardData() {
    this.textData = { ...this.internalState };
    this.dispatchNewEvent('persona-change', {
      detail: { cardData: this.textData },
    });
    this.isEditView = false;
  }

  /**
   * @returns {TemplateResult} html to render display view
   */
  _renderDisplayView = () => html`
    <div
      class=${classMap({
        'content-container': true,
        'content-placeholder': !this.textData?.content,
        content: this.textData?.content,
      })}
    >
      ${this.textData?.content
        ? html`
            <omni-wysiwyg
              @change=${this._onEditorChange}
              .initialContent=${this.textData?.content}
              ?display-only=${!this.isEditView}
            ></omni-wysiwyg>
          `
        : html`${this.textData?.title} have not yet been added`}
    </div>
  `;

  /**
   * @returns {TemplateResult} html to render edit view
   */
  _renderEditView = () => html`
    <div class="field input-container">
      <label class="label">Card title</label>
      <div class="control">
        <input
          class="input"
          type="text"
          .value=${this.internalState?.title}
          @input=${e => {
            const { value } = e.target;
            this.internalState = produce(this.internalState, draft => {
              draft.title = value;
            });
          }}
        />
      </div>
    </div>
    <div class="field text-container">
      <label class="label">Card description</label>
      <div class="control text-area">
        <omni-wysiwyg
          @change=${this._onEditorChange}
          placeholder=${this.internalState?.editorPlaceholder}
          .initialContent=${this.internalState?.content}
        ></omni-wysiwyg>
      </div>
    </div>
  `;

  /**
   * @returns {TemplateResult} main HTML to be rendered on the DOM
   */
  render() {
    return html`
      <omni-style>
        <persona-base-card
          class=${classMap({ 'edit-view': this.isEditView })}
          .isEditView=${this.isEditView}
          .readOnly=${this.readOnly}
          .cardTitle=${this.textData?.title}
          @persona-card-close=${() => {
            this.isEditView = false;
          }}
          @persona-card-submit=${this._updateCardData}
          @persona-card-edit-click=${() => {
            this.isEditView = true;
          }}
        >
          ${this.isEditView
            ? this._renderEditView()
            : this._renderDisplayView()}
        </persona-base-card>
      </omni-style>
    `;
  }
}

OmniElement.register('persona-text-card', PersonaTextCard);
